import { Injectable } from '@angular/core';
import {
	HttpEvent, HttpHandler, HttpInterceptor, HttpRequest,
	HttpResponse, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

type AnyObj = Record<string, any>;

function dumpObjectIndented(obj: any, indent = '  '): string {
	if (obj === null) return 'null';
	if (obj === undefined) return 'undefined';
	if (typeof obj === 'string') return `"${obj}"`;
	if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);
	if (Array.isArray(obj)) {
		if (obj.length === 0) return '[]';
		const items = obj.map(item => `${indent}${dumpObjectIndented(item, indent + '  ')}`);
		return `[\n${items.join(',\n')}\n${indent.slice(2)}]`;
	}
	if (typeof obj === 'object') {
		const keys = Object.keys(obj);
		if (keys.length === 0) return '{}';
		const items = keys.map(key => `${indent}${key}: ${dumpObjectIndented(obj[key], indent + '  ')}`);
		return `{\n${items.join(',\n')}\n${indent.slice(2)}}`;
	}
	return String(obj);
}

function isJsonSyntaxError(err: HttpErrorResponse): boolean {
	const e: any = err?.error;
	if (!e) return false;

	// Direct cases
	if (e instanceof SyntaxError) return true;
	if (e instanceof Error && e.name === 'SyntaxError') return true;
	if (e?.name === 'SyntaxError') return true;

	// Nested error object (e.error is the actual SyntaxError)
	const nested = e?.error;
	if (nested instanceof SyntaxError) return true;
	if (nested instanceof Error && nested.name === 'SyntaxError') return true;
	if (nested?.name === 'SyntaxError') return true;

	// Fallback to message/stack text search
	const msg = String(e?.message ?? nested?.message ?? '');
	const stack = String(e?.stack ?? nested?.stack ?? '');
	return /SyntaxError/i.test(msg) || /SyntaxError/i.test(stack);
}


@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
	private static readonly LOGIN_PATHS = ['/Login', '/LoginGlobal'];
	private static readonly POST_LOGIN_IGNORE_DURATION = 3000; // 3 seconds

	private postLoginTimestamp: number | null = null;

	constructor() {
		// Listen for successful login to track post-login state
		this.initializePostLoginTracking();
	}

	private initializePostLoginTracking(): void {
		// Set up a periodic check for new tokens (less invasive than overriding Storage)
		this.setupTokenChangeDetection();

		// Note: We don't immediately detect Auth0 callback from URL anymore
		// because the token storage might not have happened yet, especially in Safari.
		// Instead, we rely on token change detection to be more reliable.
	}

	private lastKnownToken: string | null = null;

	private setupTokenChangeDetection(): void {
		// Initialize with current token state
		this.lastKnownToken = localStorage.getItem('token') || sessionStorage.getItem('token');

		// Check for token changes every 2 seconds during potential login periods
		const checkInterval = setInterval(() => {
			const currentToken = localStorage.getItem('token') || sessionStorage.getItem('token');

			// If we detect a new token that wasn't there before, start the ignore period
			if (currentToken && !this.lastKnownToken) {
				this.postLoginTimestamp = Date.now();
				this.lastKnownToken = currentToken;
				console.log('Post-login 401 ignore period started (new token detected)');

				// Clear the interval after detecting a login to avoid continuous checking
				clearInterval(checkInterval);

				// Restart checking after the ignore period in case of multiple logins
				setTimeout(() => {
					this.setupTokenChangeDetection();
				}, ErrorInterceptor.POST_LOGIN_IGNORE_DURATION + 3000);
			} else if (!currentToken && this.lastKnownToken) {
				// Reset if token is removed (logout)
				this.lastKnownToken = null;
				this.postLoginTimestamp = null;
				console.log('Token removed, resetting post-login tracking');
			} else if (currentToken) {
				// Update known token if it changes
				this.lastKnownToken = currentToken;
			}
		}, 2000);

		// Clean up the interval after a reasonable time if no login is detected
		setTimeout(() => {
			clearInterval(checkInterval);
		}, 15000); // Stop checking after 15 seconds
	}

	private shouldIgnore401Error(url: string | null): boolean {
		if (!url) return false;

		const currentPath = window.location.pathname;

		// Always ignore 401s when we're on login pages
		const isOnLoginPage = ErrorInterceptor.LOGIN_PATHS.some(path =>
			currentPath === path || currentPath.includes(path)
		);

		if (isOnLoginPage) {
			console.log('Ignoring 401 error while on login page:', currentPath);
			return true;
		}

		// Also ignore 401s for requests that are explicitly to login endpoints
		const isLoginRequest = ErrorInterceptor.LOGIN_PATHS.some(path =>
			url.includes(path)
		);

		if (isLoginRequest) {
			console.log('Ignoring 401 error for login request:', url);
			return true;
		}

		// Ignore 401s for a short period after login (when token is detected)
		if (this.postLoginTimestamp) {
			const timeSinceLogin = Date.now() - this.postLoginTimestamp;
			if (timeSinceLogin < ErrorInterceptor.POST_LOGIN_IGNORE_DURATION) {
				console.log(`Ignoring 401 error ${timeSinceLogin}ms after login:`, url);
				return true;
			} else {
				// Clear the timestamp after the ignore period
				this.postLoginTimestamp = null;
			}
		}

		return false;
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(req).pipe(
			// 1) Successful responses: show toast if body.errorMessage exists (migrated from AngularJS)
			tap(evt => {
				if (evt instanceof HttpResponse) {
					const body: AnyObj | undefined = evt.body;
					const msg = body?.errorMessage as string | undefined;
					if (msg) {
						console.log('Found errorMessage in successful response:', msg);
						// Show toast at top-left to match AngularJS $mdToast behavior
						this.showCustomToast(msg, 'top');
						// Convert successful response with errorMessage to error (matching AngularJS behavior)
						throw new HttpErrorResponse({
							error: body, headers: evt.headers, status: evt.status,
							statusText: evt.statusText, url: evt.url || undefined
						});
					}
				}
			}),

			// 2) Error responses
			catchError((err: HttpErrorResponse) => {
				console.error('HTTP Error in NoInjectorErrorInterceptor:', err);

				// 0) Ignore bad-JSON/SyntaxError responses entirely (no toast)
				if (isJsonSyntaxError(err)) {
					return EMPTY;
				}


				// Skip error handling for 401s on login paths or shortly after login
				if (err.status === 401 && this.shouldIgnore401Error(err.url)) {
					console.log('Skipping 401 error handling due to login context');
					return	EMPTY;
				}

				const w = (window as any);
				const SdmGlobal = w.SdmGlobal || {
					isMobile: () => /android|iphone|ipad|mobile|blackberry|iemobile/i.test(navigator.userAgent),
					getEnv: () => 'd0'
				};

				const body = (err.error && typeof err.error === 'object') ? err.error : {};
				const errorObj = (body.error ?? body) as AnyObj;

				// Special handling: TenantUndefined - but NO StateService navigation (avoid all dependencies)
				if (errorObj?.ErrorCode === 'TenantUndefined') {
					console.warn('TenantUndefined error - would normally navigate but avoiding StateService dependency');
					// Could use window.location.href for navigation if needed
					// window.location.href = '#/SetCurrentTenant';
				}

				const exceptionName = errorObj?.ExceptionName;
				const isSecurityTokenExpired = exceptionName === 'SecurityTokenExpiredException';

				// BrowserTest quick alert (legacy parity)
				const xEnv = err.headers?.get('X-Environment');
				if (!isSecurityTokenExpired && !SdmGlobal.isMobile() && xEnv === 'BrowserTest') {
					alert('Error');
					return throwError(() => err);
				}

				// Developer helper window when running locally (env d0)
				if (!isSecurityTokenExpired) {
					const isLocalhost = (SdmGlobal.getEnv && SdmGlobal.getEnv() === 'd0');
					const isDorLocalhost = (SdmGlobal.getEnv && (SdmGlobal.getEnv() === 'd0' || SdmGlobal.getEnv() === 'd'));

					if (isLocalhost) {
						const mybody = dumpObjectIndented({ url: err.url, status: err.status, body: err.error });
						w.showLatestError = () => {
							const ref = window.open('', isDorLocalhost ? 'sdmErrorWindow' : '_blank');
							if (!ref) return;
							ref.document.close();
							ref.document.write(`<html><head><title>SDM Error Page</title></head><body><pre>${mybody}</pre></body></html>`);
						};
						if (!SdmGlobal.isMobile()) {
							w.showLatestError();
						}
					}

					// Build user-facing message (simplified)
					const traceId = errorObj?.TraceId;
					let msg = '';
					if (!errorObj || !errorObj.ErrorCode) {
						msg = 'Unknown error, please contact your system administrator.';
					} else {
						msg = `Error: ${errorObj.ErrorCode}`;
						if (traceId) {
							msg += ` (Trace ID: ${traceId})`;
						}
					}

					this.showCustomToast(msg, 'bottom', () => {
						this.reportError(traceId, err);
					});
				}

				return throwError(() => err);
			})
		);
	}

	private showCustomToast(message: string, position: 'top' | 'bottom', onAction?: () => void): void {
		console.error(`Toast: ${message}`);

		// Create full-width toast with dismiss button on the right
		const toast = document.createElement('div');
		toast.style.position = 'fixed';
		toast.style.minHeight = '48px';
		toast.style.width = '100%';
		toast.style.left = '0';
		toast.style.right = '0';
		toast.style.padding = '14px 24px';
		toast.style.backgroundColor = '#323232';
		toast.style.color = '#ffffff';
		toast.style.fontSize = '14px';
		toast.style.fontFamily = 'Roboto, "Helvetica Neue", sans-serif';
		toast.style.fontWeight = '400';
		toast.style.lineHeight = '20px';
		toast.style.boxShadow = '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)';
		toast.style.zIndex = '10000';
		toast.style.opacity = '0';
		toast.style.transition = 'opacity 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)';
		toast.style.display = 'flex';
		toast.style.alignItems = 'center';
		toast.style.justifyContent = 'space-between';

		if (position === 'top') {
			toast.style.top = '0';
		} else {
			toast.style.bottom = '0';
		}

		// Message container
		const messageSpan = document.createElement('span');
		messageSpan.textContent = message;
		messageSpan.style.flex = '1';
		messageSpan.style.fontSize = '14px';
		messageSpan.style.fontWeight = '400';
		messageSpan.style.lineHeight = '20px';
		messageSpan.style.color = '#ffffff';
		toast.appendChild(messageSpan);

		// Action buttons container (positioned on the right)
		const actionsContainer = document.createElement('div');
		actionsContainer.style.display = 'flex';
		actionsContainer.style.alignItems = 'center';
		actionsContainer.style.gap = '8px';
		actionsContainer.style.marginLeft = 'auto';
		actionsContainer.style.flexShrink = '0';

		// Dismiss button (always present) - WHITE color
		const dismissButton = document.createElement('button');
		dismissButton.textContent = 'DISMISS';
		dismissButton.style.backgroundColor = 'transparent';
		dismissButton.style.border = 'none';
		dismissButton.style.color = '#ffffff'; // White color as requested
		dismissButton.style.cursor = 'pointer';
		dismissButton.style.fontSize = '14px';
		dismissButton.style.fontWeight = '500';
		dismissButton.style.fontFamily = 'Roboto, "Helvetica Neue", sans-serif';
		dismissButton.style.textTransform = 'uppercase';
		dismissButton.style.padding = '8px 8px';
		dismissButton.style.minWidth = '64px';
		dismissButton.style.minHeight = '36px';
		dismissButton.style.borderRadius = '4px';
		dismissButton.style.transition = 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)';
		dismissButton.style.letterSpacing = '0.0892857143em';

		// Hover effect for dismiss - white with opacity
		dismissButton.onmouseenter = () => {
			dismissButton.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
		};
		dismissButton.onmouseleave = () => {
			dismissButton.style.backgroundColor = 'transparent';
		};

		dismissButton.onclick = () => {
			toast.style.opacity = '0';
			setTimeout(() => {
				toast.remove();
			}, 300);
		};

		// Add REPORT ERROR button first (if present) - PINK color
		if (onAction) {
			const reportButton = document.createElement('button');
			reportButton.textContent = 'REPORT ERROR';
			reportButton.style.backgroundColor = 'transparent';
			reportButton.style.border = 'none';
			reportButton.style.color = '#E91E63'; // Pink color as requested
			reportButton.style.cursor = 'pointer';
			reportButton.style.fontSize = '14px';
			reportButton.style.fontWeight = '500';
			reportButton.style.fontFamily = 'Roboto, "Helvetica Neue", sans-serif';
			reportButton.style.textTransform = 'uppercase';
			reportButton.style.padding = '8px 8px';
			reportButton.style.minWidth = '64px';
			reportButton.style.minHeight = '36px';
			reportButton.style.borderRadius = '4px';
			reportButton.style.transition = 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)';
			reportButton.style.letterSpacing = '0.0892857143em';

			// Hover effect for report - pink with opacity
			reportButton.onmouseenter = () => {
				reportButton.style.backgroundColor = 'rgba(233, 30, 99, 0.08)';
			};
			reportButton.onmouseleave = () => {
				reportButton.style.backgroundColor = 'transparent';
			};

			reportButton.onclick = onAction;
			actionsContainer.appendChild(reportButton);
		}

		// Add DISMISS button last (always on the far right)
		actionsContainer.appendChild(dismissButton);

		toast.appendChild(actionsContainer);

		document.body.appendChild(toast);

		// Fade in
		setTimeout(() => {
			toast.style.opacity = '1';
		}, 10);

		// Fade out after a delay (or keep indefinitely if action button is present)
		if (!onAction) {
			setTimeout(() => {
				toast.style.opacity = '0';
				setTimeout(() => {
					toast.remove();
				}, 500); // Remove after transition
			}, 5000); // Display duration
		}
	}

	private reportError(traceId: string | undefined, error: HttpErrorResponse): void {
		// Replicate ToastController.reportError functionality using native browser APIs
		const title = traceId ?
			`Report error (trace-ID is ${traceId})` :
			'Report error';

		const message = 'Do you want to add information, please add it here or leave it blank. The trace-ID will be included automatically.';

		const userComment = prompt(message, '');

		if (userComment !== null) { // User didn't cancel
			// Prepare error data similar to ToastController
			const errorData = {
				traceId: traceId || '',
				comment: userComment,
				url: error.url,
				status: error.status,
				error: error.error
			};

			// Make HTTP request to report error (using fetch to avoid HttpClient dependency)
			// Get authentication token and other headers that would normally be added by interceptors

			// Try to get token from AngularJS store first (like JwtHttpInterceptor does)
			let token: string | null = null;
			try {
				const injector = (window as any).angular?.element(document.body).injector();
				const store = injector?.get('store');
				token = store ? store.get('token') : null;
			} catch (e) {
				console.warn('Could not access AngularJS store, falling back to localStorage');
			}

			// Fallback to localStorage/sessionStorage
			if (!token) {
				token = localStorage.getItem('token') || sessionStorage.getItem('token');
			}

			const tenant = localStorage.getItem('tenant');
			const clientTraceId = localStorage.getItem('clientTraceId');
			const clientInfo = localStorage.getItem('clientInfo');

			const headers: Record<string, string> = {
				'Content-Type': 'application/json',
			};

			if (token) {
				headers['Authorization'] = `Bearer ${token}`;
			}

			if (tenant) {
				headers['X-Global-Tenant'] = tenant;
			}

			if (clientTraceId) {
				headers['X-Client-TraceId'] = clientTraceId;
			}

			if (clientInfo) {
				headers['X-Client-Info'] = clientInfo;
			}

			console.log('Reporting error with headers:', headers);
			console.log('Error data:', errorData);

			fetch('api/Support/ReportError', {
				method: 'POST',
				headers: headers,
				body: JSON.stringify(errorData)
			})
				.then(response => {
					if (response.ok) {
						// Show success notification
						this.showCustomToast('Error reported successfully', 'top');
					} else {
						console.error('Failed to report error:', response.statusText);
						this.showCustomToast('Failed to report error', 'top');
					}
				})
				.catch(err => {
					console.error('Error reporting failed:', err);
					this.showCustomToast('Failed to report error', 'top');
				});
		}
	}
}
